import type { MetricKey, PerfRow } from "../types";

import { BottomSheet } from "../../BottomSheet";
import { DocumentIcon, ExpandIcon, GlobeIcon, LinkIcon } from "../../Icons";
import { Typo } from "../../Typo";
import { BannerThumb } from "../components/BannerThumb";
import { CountryFlag } from "../components/CountryFlag";
import {
  fmtInt,
  fmtPct,
  fmtUsd,
  hasMetric,
  metricVal,
  rowLabel,
} from "../helpers";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";

const SectionHeader = ({
  row,
  rebateAccountLabel,
  countryUnknown,
  noCampaign,
}: {
  row: PerfRow;
  rebateAccountLabel: string;
  countryUnknown: string;
  noCampaign: string;
}) => {
  const icon =
    row.type === "banner" ? (
      <BannerThumb row={row} />
    ) : row.type === "country" ? (
      <CountryFlag code={row.code} size="md" />
    ) : row.type === "link" ? (
      <div className="flex h-10 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-brand-subtle text-icon-brand-default">
        <LinkIcon
          className="h-4 w-4 [&_path]:stroke-icon-brand-default [&_path]:stroke-1 [&_path]:[vector-effect:non-scaling-stroke]"
          aria-hidden="true"
          focusable="false"
        />
      </div>
    ) : null;

  const metaItems = [
    row.type === "link" ? { Icon: DocumentIcon, value: row.page } : null,
    row.type === "banner" ? { Icon: ExpandIcon, value: row.dim } : null,
    { Icon: GlobeIcon, value: row.lang },
  ]
    .filter((item) => item?.value)
    .map((item) => item!);

  const title =
    row.type === "banner"
      ? rowLabel(row.name, row, countryUnknown, noCampaign)
      : [
          rowLabel(row.name, row, countryUnknown, noCampaign),
          row.type === "plan" ? row.currency : null,
        ]
          .filter((v) => v)
          .join(" · ");

  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-3 overflow-hidden">
      {icon}
      <Typo
        element="span"
        type="labelSM"
        textColor="text-text-primary"
        className="min-w-0 flex-1"
      >
        {title}
      </Typo>
      {row.type === "plan" && row.account && (
        <div className="flex items-center gap-1 text-text-secondary">
          <Typo element="span" type="captionXS" textColor="text-text-secondary">
            {rebateAccountLabel}:
          </Typo>
          <Typo element="span" type="captionXS" textColor="text-text-secondary">
            {row.account}
          </Typo>
        </div>
      )}
      {metaItems.length > 0 && (
        <div className="flex items-center gap-3 text-text-secondary">
          {metaItems.map(({ Icon, value }) => (
            <span
              className="inline-flex shrink-0 items-center gap-1"
              key={value}
            >
              <Icon
                className="h-3.5 w-3.5 text-text-secondary"
                aria-hidden="true"
                focusable="false"
              />{" "}
              <Typo
                element="span"
                type="captionXS"
                textColor="text-text-secondary"
              >
                {value}
              </Typo>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const SectionDivider = () => (
  <div className="h-px bg-stroke-primary" role="separator" />
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typo
    element="span"
    type="labelSM"
    textColor="text-text-primary"
    className="block"
  >
    {children}
  </Typo>
);

const MetricLine = ({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  /** Trailing unit (e.g. "USD"). Rendered inside the value's own `Typo` so it
   * inherits the identical size and colour, per the design — hence no nested
   * `Typo`, unlike the de-emphasised unit on the table cells and KPI cards. */
  unit?: string;
}) => (
  <div className="flex items-baseline justify-between gap-4">
    <Typo element="span" type="bodySM" textColor="text-text-secondary">
      {label}
    </Typo>
    <Typo
      element="span"
      type="labelSM"
      textColor="text-text-primary"
      className="tabular-nums"
    >
      {unit ? `${value} ${unit}` : value}
    </Typo>
  </div>
);

/** Nested conversion-rate line beneath a funnel stat — visually smaller than
 * the stat itself (canvas: `m-pd__subk`/`m-pd__subv`, both `typo-caption-xs`). */
const ConversionLine = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-4">
    <Typo element="span" type="captionXS" textColor="text-text-secondary">
      {label}
    </Typo>
    <Typo
      element="span"
      type="captionXS"
      textColor="text-text-primary"
      className="tabular-nums"
    >
      {value}
    </Typo>
  </div>
);

/** Funnel stat, paired with the conversion (%) metric shown as its subline. */
const TRAFFIC_STATS: { key: MetricKey; convKey?: MetricKey }[] = [
  { key: "impressions" },
  { key: "clicks" },
  { key: "registration", convKey: "cr_reg" },
  { key: "approved", convKey: "cr_app" },
  { key: "funded", convKey: "cr_funded" },
  { key: "traded", convKey: "cr_traded" },
];

type PerformanceDetailScreenProps = {
  row: PerfRow | null;
  onClose: () => void;
};

/**
 * Source detail screen (AC-8). Two sections: "Traffic statistics" (each
 * funnel count with its Click→stage conversion nested directly beneath it)
 * and "Trading summary". The updated AC-8 (edited 2026-07 after the
 * design-vs-AC conflict was ruled on) confirms this funnel-with-nested-
 * conversion + trading-summary structure and no in-screen CSV export. The
 * design calls for a full push screen with a
 * drag handle; there's no push-screen primitive in this codebase, so this
 * reuses `BottomSheet`'s drag-handle/swipe-to-dismiss at near-full height as
 * a deliberate stand-in — see `PART-3345-plan.md` → Approach. No CSV export
 * lives in this screen — neither the Jira AC8 text nor the live canvas
 * includes one here (a prior version of this component added one that
 * wasn't actually in either source).
 */
export const PerformanceDetailScreen = ({
  row,
  onClose,
}: PerformanceDetailScreenProps) => {
  const translations = usePerformanceReportTranslations();

  if (!row) return null;

  const colByKey = new Map(
    translations.metricColumns.map((c) => [c.key, c] as const),
  );
  const volumeCol = colByKey.get("volume");
  const earningsCol = colByKey.get("earnings");

  return (
    <BottomSheet
      isOpen={Boolean(row)}
      onClose={onClose}
      isDraggable
      title={translations.mobileDetailTitle}
    >
      <div className="flex flex-col gap-4">
        <SectionHeader
          row={row}
          rebateAccountLabel={translations.mobileDetailRebateAccount}
          countryUnknown={translations.countryUnknown}
          noCampaign={translations.noCampaign}
        />

        <SectionDivider />
        <SectionTitle>{translations.mobileDetailTraffic}</SectionTitle>
        {TRAFFIC_STATS.filter((f) => hasMetric(row, f.key)).map((f) => {
          const metricCol = colByKey.get(f.key);
          const convCol = f.convKey ? colByKey.get(f.convKey) : undefined;
          if (!metricCol) return null;

          return (
            <div key={f.key} className="flex flex-col gap-1">
              <MetricLine
                label={metricCol.label}
                value={fmtInt(metricVal(row, f.key))}
              />
              {convCol && f.convKey && (
                <ConversionLine
                  label={convCol.menuLabel ?? convCol.label}
                  value={fmtPct(metricVal(row, f.convKey))}
                />
              )}
            </div>
          );
        })}

        <SectionDivider />
        <SectionTitle>{translations.mobileDetailTradingSummary}</SectionTitle>
        {volumeCol && (
          <MetricLine
            label={volumeCol.label}
            value={fmtUsd(metricVal(row, "volume"))}
            unit={translations.unitUsd}
          />
        )}
        {earningsCol && (
          <MetricLine
            label={earningsCol.label}
            value={fmtUsd(metricVal(row, "earnings"))}
            unit={translations.unitUsd}
          />
        )}
      </div>
    </BottomSheet>
  );
};
